import { t } from 'i18next';

import { multilineTextToArray, getCurrencyString, getValidString } from './StringHelper';

import cf = egr.wcf.cf;
import eaiws = egr.wcf.eaiws;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import bsk = egr.wcf.bsk;

/**
 * LIST = calculationSheetInfo.salesPrice
 * OFFER = calculationSheetInfo.netPrice
 */
export type PriceVisibility = 'NONE' | 'LIST' | 'OFFER' | 'OFFER_WITH_DISCOUNT' | 'GROSS';

export interface PriceInfo {
    unitSales: string | null;
    fullSales: string | null;
    discount: string | null;
    fullNet: string | null;
}

export function getArticleHeader(articleProperties: eaiws.basket.ArticleProperties): string {
    const outputArray: Array<string> = [];
    const articleNumber: string | undefined = articleProperties.useFinalArticleNumber ?
        articleProperties.finalArticleNumber : articleProperties.baseArticleNumber;
    if (utils.string.isNotNullOrEmpty(articleNumber)) {
        outputArray.push(articleNumber);
    }
    if (utils.string.isNotNullOrEmpty(articleProperties.shortText)) {
        outputArray.push(articleProperties.shortText);
    }
    return outputArray.join(' | ');
}

export function getManufacturerHeader(articleProperties: eaiws.basket.ArticleProperties): string {
    const outputArray: Array<string> = [];
    if (utils.string.isNotNullOrEmpty(articleProperties.manufacturerName)) {
        outputArray.push(articleProperties.manufacturerName);
    }
    if (utils.string.isNotNullOrEmpty(articleProperties.seriesId)) {
        outputArray.push(articleProperties.seriesId);
    }
    return outputArray.join(' | ');
}

export function getDescription(articleProperties: eaiws.basket.ArticleProperties): string {
    let description: Array<string> = [];
    if (utils.string.isNotNullOrEmpty(articleProperties.longText)) {
        description.push(articleProperties.longText + '\n');
    }
    if (utils.string.isNotNullOrEmpty(articleProperties.featureText)) {
        description = description.concat(multilineTextToArray(articleProperties.featureText));
    }
    return description.join('\n');
}

export function getPriceInfo(
    calculationSheet: eaiws.basket.CalculationSheetInfo | undefined,
    priceVisibility: PriceVisibility,
    language: string,
    hideCurrency: boolean = false
): PriceInfo | null {
    if (calculationSheet == null || priceVisibility === 'NONE') {
        return null;
    }

    const unitSales: string | null = getPriceString(calculationSheet.unitSalesPrice, language, 'code', hideCurrency);
    const fullSales: string | null = getPriceString(calculationSheet.salesPrice, language, 'code', hideCurrency);
    const fullNet: string | null = getPriceString(calculationSheet.netPrice, language, 'code', hideCurrency);

    switch (priceVisibility) {
        case 'LIST': {
            return {
                unitSales,
                fullSales,
                discount: null,
                fullNet: null
            };
        }
        case 'OFFER': {
            return {
                unitSales,
                fullSales,
                discount: null,
                fullNet
            };
        }
        case 'OFFER_WITH_DISCOUNT': {
            return {
                unitSales,
                fullSales,
                discount: null, // TODO
                fullNet
            };
        }
        default: {
            return null;
        }
    }
}

export function getPriceString(
    value: eaiws.basket.Money | undefined,
    language: string = 'de',
    currencyDisplay: 'symbol' | 'name' | 'code' = 'symbol',
    hideCurrency: boolean = false
): string | null {
    if (value == null || value.value == null) {
        return null;
    }

    return getCurrencyString(value.value, value.currency, language, currencyDisplay, hideCurrency);
}

export function exclusiveTaxes(value: PriceVisibility | string | undefined): boolean {
    return (
        value === 'LIST' ||
        value === 'OFFER' ||
        value === 'OFFER_WITH_DISCOUNT'
    );
}

export function toPriceVisibilityLabel(value: PriceVisibility | string | undefined): string {
    switch (value) {
        case 'LIST':
            return t('Show list prices');
        case 'OFFER':
            return t('Show offer prices without discounts');
        case 'OFFER_WITH_DISCOUNT':
            return t('Show offer prices with discounts');
        case 'GROSS':
            return t('Show gross prices');
        default: {
            return t('Show no prices');
        }
    }
}
export function toPriceVisiblity(value: string): PriceVisibility {
    if (value !== 'NONE' && value !== 'LIST' && value !== 'OFFER' && value !== 'OFFER_WITH_DISCOUNT') {
        return 'NONE';
    }
    return value;
}

export function getNetPriceOfCalculation(calculation: eaiws.basket.CalculationSheetInfo | undefined): eaiws.basket.Money | null {
    if (
        calculation == null ||
        calculation.netPrice == null
    ) {
        return null;
    }
    return calculation.netPrice;
}

export function getPriceOfCalculationSheet(
    calculationSheet: eaiws.basket.CalculationSheetInfo | undefined, unit: boolean = true, priceVisibility?: PriceVisibility
): eaiws.basket.Money | undefined {
    if (calculationSheet == null) {
        return;
    }

    if (priceVisibility === 'LIST') {
        if (unit) {
            return calculationSheet.unitSalesPrice; // direkt aus den daten
        }

        return calculationSheet.salesPrice;
    }

    if (priceVisibility === 'OFFER' || priceVisibility === 'OFFER_WITH_DISCOUNT') {
        if (unit) {
            return calculationSheet.unitNetPrice; // mit rabatten
        }

        return calculationSheet.netPrice;
    }

    if (priceVisibility === 'GROSS') {
        if (unit) {
            return calculationSheet.unitGrossPrice; // mit steuern + rabatten
        }

        return calculationSheet.grossPrice;
    }

    return;
}

export function getPriceValue(element: cf.ArticleElement | bsk.ViewItem | null, unit: boolean = true, priceVisibility?: PriceVisibility): number | undefined {
    if (element == null || priceVisibility === 'NONE') {
        return;
    }
    const calculationSheet: eaiws.basket.CalculationSheetInfo | undefined = element.getCachedCalculation();
    if (calculationSheet == null) {
        return;
    }
    const price: eaiws.basket.Money | undefined = getPriceOfCalculationSheet(calculationSheet, unit, priceVisibility);
    if (price == null) {
        return;
    }
    return price.value;
}

export function getCurrency(element: cf.ArticleElement | bsk.ViewItem | null): string | null {
    if (element == null) {
        return null;
    }
    const calculationSheet: eaiws.basket.CalculationSheetInfo | undefined = element.getCachedCalculation();
    if (
        calculationSheet !== undefined &&
        calculationSheet.unitNetPrice !== undefined &&
        calculationSheet.unitNetPrice.value !== undefined
    ) {
        return calculationSheet.unitNetPrice.currency;
    }
    return null;
}

export function propertyValueWithImage(propertyValue: core.prop.PropertyValue): boolean {
    return utils.string.isNotNullOrEmpty(propertyValue.largeIcon) || utils.string.isNotNullOrEmpty(propertyValue.smallIcon);
}

/**
 * Returns i18next translated string of quantity's unit (UN/CEFACT Code).
 * At the moment only 'C64' (UN/CEFACT Code for piece) is returned, add more if other units appear in eaiws data.
 * If item has no quantity unit undefined is returned
 * @param item
 */
export function getQuantityTypeString(item: bsk.ViewItem): string | undefined {
    const calculationSheetInfo: eaiws.basket.CalculationSheetInfo | undefined = item.getCachedCalculation();
    if (calculationSheetInfo == null) {
        return;
    }
    const quantity: eaiws.basket.Quantity | undefined = calculationSheetInfo.sheet.quantity;
    if (quantity == null) {
        return;
    }
    switch (quantity.unit) {
        case 'C62': return t('C62', {context: 'UN/CEFACT Code, max. 3 letters please'});
        default: return;
    }
}

export function getInclusiveInfos(
    subProperties: ReadonlyArray<eaiws.basket.ArticleProperties>, language: string = 'en', withPrices: boolean = false
): Map<string, number> {
    const result: Map<string, number> = new Map<string, number>();
    subProperties.forEach((child) => {
        const childInfo: {label: string, count: number} = getChildInfo(child, language, withPrices);
        const existingCount: number | undefined = result.get(childInfo.label);
        if (existingCount == null) {
            result.set(childInfo.label, childInfo.count);
        } else {
            result.set(childInfo.label, existingCount + childInfo.count);
        }
    });
    return result;
}

export function getInclusiveInfosList(itemProps: eaiws.basket.ItemProperties | undefined, language: string = 'en', withPrices: boolean = false): Array<string> {
    if (
        itemProps == null ||
        itemProps.article == null ||
        itemProps.article.subArticles == null ||
        !itemProps.article.subArticles.length
    ) {
        return [];
    }
    const subProperties: ReadonlyArray<eaiws.basket.ArticleProperties> = itemProps.article.subArticles.reduce(concatArticleProperties, []);
    const infosMap: Map<string, number> = getInclusiveInfos(subProperties, language, withPrices);
    const result: Array<string> = [];
    infosMap.forEach((value: number, key: string) => {
        result.push(`${value} x ${key}`);
    });
    return result;
}

export async function getInclusiveInfosListFromConfiguration(
    item: cf.MainArticleElement, language: string = 'en', withPrices: boolean = false
): Promise<Array<string>> {
    const subArticles: Array<cf.SubArticleElement> = item.getSubArticles(false);
    const subProperties: Array<eaiws.basket.ArticleProperties> = [];
    for (const subArticle of subArticles) {
        const itemProps: eaiws.basket.ItemProperties | undefined = await subArticle.getItemProperties();
        if (itemProps != null && itemProps.article != null) {
            subProperties.push(itemProps.article);
        }
    }
    const infosMap: Map<string, number> = getInclusiveInfos(subProperties, language, withPrices);
    const result: Array<string> = [];
    infosMap.forEach((value: number, key: string) => {
        result.push(`${value} x ${key}`);
    });
    return result;
}

/**
 * Will return empty string if no articlenumber is defined in properties.
 * @param properties
 */
export function getArticleNumber(properties: eaiws.basket.ArticleProperties): string {
    if (
        properties.useFinalArticleNumber !== undefined &&
        properties.useFinalArticleNumber === true &&
        properties.finalArticleNumber !== undefined
    ) {
        return ` | ${properties.finalArticleNumber}`;
    }

    if (properties.baseArticleNumber !== undefined) {
        return ` | ${properties.baseArticleNumber}`;
    }

    return '';
}

export function getItemHeader(articleProperties: eaiws.basket.ArticleProperties | null | undefined): string | undefined;
export function getItemHeader(articleProperties: eaiws.basket.ArticleProperties | null | undefined, fallback: string): string;
export function getItemHeader(articleProperties: eaiws.basket.ArticleProperties | null | undefined, fallback?: string): string | undefined {
    if (articleProperties == null) {
        return fallback;
    }

    const subParts: Array<string | undefined> = [
        articleProperties.manufacturerName,
        articleProperties.seriesName,
        articleProperties.baseArticleNumber
    ];

    const finalString: string | undefined = subParts.filter(utils.string.isNotNullOrEmpty).join(' | ');

    if (utils.string.isNullOrEmpty(finalString)) {
        return fallback;
    }
    return finalString;
}

// tslint:disable-next-line:cyclomatic-complexity
export function getItemLabel(properties: eaiws.basket.ArticleProperties | undefined, fallback: string = ''): string {
    if (properties == null) {
        return fallback;
    }

    switch (properties.descriptionMode) {
        case 'Long':
            if (utils.string.isNotNullOrEmpty(properties.longText)) {
                return properties.longText;
            }
            break;
        case 'Short':
            if (utils.string.isNotNullOrEmpty(properties.shortText)) {
                return properties.shortText;
            }
            break;
        case 'Both':
            if (utils.string.isNullOrEmpty(properties.shortText) && utils.string.isNullOrEmpty(properties.longText)) {
                return fallback;
            }
            if (utils.string.isNullOrEmpty(properties.shortText)) {
                return properties.longText!;
            }
            if (utils.string.isNullOrEmpty(properties.longText)) {
                return properties.shortText;
            }
            return `${properties.shortText}\n\n${properties.longText}`;
        default:
            break;
    }
    return fallback;
}

export function getChildInfo(
    properties: eaiws.basket.ArticleProperties,
    language: string = 'en',
    withPrices?: boolean
): {label: string, count: number} {
    const articleNumberPart: string = getArticleNumber(properties);

    const priceInfo: eaiws.basket.PriceInfo | undefined = properties.priceInfo;
    const count: number = properties.priceInfo == null ? 1 : properties.priceInfo.quantity;
    if (withPrices && priceInfo != null && priceInfo.quantity != null) {
        if (priceInfo.netPrice != null && priceInfo.netPrice.value != null) {
            const singlePriceString: string = getCurrencyString(priceInfo.netPrice.value, priceInfo.netPrice.currency, language, 'code');
            const singlePricePart: string = ` | ${singlePriceString} (${t('SP', {context: 'short for singleprice'})})`;
            if (priceInfo.quantity > 1 && priceInfo.netValue != null) {
                const totalPriceString: string = getCurrencyString(priceInfo.netValue.value, priceInfo.netValue.currency, language, 'code');
                const totalPricePart: string = ` | ${totalPriceString} (${t('TP', { context: 'short for totalprice'})})`;
                return {label: `${getItemLabel(properties)}${articleNumberPart}${singlePricePart}${totalPricePart}`, count};
            }
            return {label: `${getItemLabel(properties)}${articleNumberPart}${singlePricePart}`, count};
        }
        return {label: `${getItemLabel(properties)}${articleNumberPart}`, count};
    }
    return {label: `${getItemLabel(properties)}${articleNumberPart}`, count};
}

export function concatArticleProperties(
    previousValue: ReadonlyArray<eaiws.basket.ArticleProperties>, currentValue: eaiws.basket.ArticleProperties
): ReadonlyArray<eaiws.basket.ArticleProperties> {
    if (currentValue.subArticles == null) {
        return previousValue.concat([currentValue]);
    }
    return previousValue.concat([currentValue], currentValue.subArticles.reduce(concatArticleProperties, []));
}

export function reduceBasketItemsWithChildren<T extends bsk.ViewItem = bsk.ViewItem>(
    value: ReadonlyArray<T>,
    skipStandardItems: boolean
): ReadonlyArray<T> {
    if (skipStandardItems) {
        return value.reduce(reduceBasketContainerItems, []);
    }
    return value.reduce(reduceBasketItems, []);
}

export interface ItemDescription {
    label: Array<string>;
    text: Array<string>;
    features: Array<string>;
    inclusiveInfo: Array<string>;
}

export async function getItemDescription(
    item: cf.MainArticleElement | bsk.ViewItem,
    itemProperties: eaiws.basket.ItemProperties,
    language: string,
    priceVisibility: PriceVisibility,
    addInclusiveInfos: boolean = false
): Promise<ItemDescription | undefined> {
    const result: ItemDescription = {label: [], text: [], features: [], inclusiveInfo: []};

    if (itemProperties.article != null) {
        result.label = multilineTextToArray(getItemLabel(itemProperties.article, ''), true, true, true);
        // add longtext if not already in label
        if (itemProperties.article.descriptionMode === 'Short') {
            result.text = multilineTextToArray(itemProperties.article.longText, true, true, true);
        }
        result.features = multilineTextToArray(itemProperties.article.featureText, true, true, true);
    }

    if (addInclusiveInfos) {
        if (item instanceof cf.MainArticleElement) {
            result.inclusiveInfo = await getInclusiveInfosListFromConfiguration(item, language, priceVisibility !== 'NONE');
        } else {
            result.inclusiveInfo = getInclusiveInfosList(itemProperties, language, priceVisibility !== 'NONE');
        }
    }

    return result;
}

function reduceBasketItems<T extends bsk.ViewItem = bsk.ViewItem>(previous: ReadonlyArray<T>, value: T, index: number): ReadonlyArray<T> {
    value.userData.setData('export', 'index', `${index + 1}`);
    return previous.concat(
        [value],
        (value.children as ReadonlyArray<T>).reduce(reduceChildren(`${index + 1}`), [])
    );
}

function reduceBasketContainerItems<T extends bsk.ViewItem = bsk.ViewItem>(previous: ReadonlyArray<T>, value: T, index: number): ReadonlyArray<T> {
    if (value.type === 'Folder' || value.type === 'SetArticle') {
        value.userData.setData('export', 'index', `${index + 1}`);
        return previous.concat(
            [value],
            (value.children as ReadonlyArray<T>).reduce(reduceChildren(`${index + 1}`), [])
        );
    }
    return previous.concat([value]);
}

function reduceChildren<T extends bsk.ViewItem = bsk.ViewItem>(labelIndex: string): (previous: ReadonlyArray<T>, value: T, index: number) => ReadonlyArray<T> {
    return (previous: ReadonlyArray<T>, value: T, index: number): ReadonlyArray<T> => {
        value.userData.setData('export', 'index', `${labelIndex}.${index + 1}`);
        if (value.children.length === 0) {
            return previous.concat([value]);
        }
        return previous.concat(
            [value],
            (value.children as ReadonlyArray<T>).reduce(reduceChildren(`${labelIndex}.${index + 1}`), [])
        );
    };
}

export function getExchangeRate(
    beforeCurrency: string | null,
    afterCurrency: string | null,
    rates: Array<eaiws.basket.ExchangeRate>
): number {
    if (utils.string.isNullOrEmpty(beforeCurrency) || utils.string.isNullOrEmpty(afterCurrency)) {
        return 1;
    }

    let result: number = 1;
    if (beforeCurrency === 'EUR' && afterCurrency !== 'EUR') {
        rates.forEach((value: eaiws.basket.ExchangeRate) => {
            if (value.currency === afterCurrency && utils.number.isNumber(value.rate)) {
                result = value.rate;
            }
        });
    } else if (beforeCurrency !== 'EUR' && afterCurrency === 'EUR') {
        rates.forEach((value: eaiws.basket.ExchangeRate) => {
            if (value.currency === beforeCurrency && utils.number.isNumber(value.rate)) {
                result = 1 / value.rate;
            }
        });
    } else {
        let stepOne: number = 1;
        let stepTwo: number = 1;

        rates.forEach((value: eaiws.basket.ExchangeRate) => {
            if (value.currency === beforeCurrency && utils.number.isNumber(value.rate)) {
                stepOne = value.rate;
            }
            if (value.currency === afterCurrency && utils.number.isNumber(value.rate)) {
                stepTwo = 1 / value.rate;
            }
        });

        result = stepOne * stepTwo;
    }
    return result;
}

/**
 * Handles missing currency information by returning first child/subArticle currency
 * @param item
 */
export function getItemCurrency(item: bsk.ViewItem): string | undefined {
    const itemProperties: eaiws.basket.ItemProperties | undefined = item.getCachedItemProperties();
    if (itemProperties == null || itemProperties.article == null) {
        return;
    }

    // Default
    if (utils.string.isNotNullOrEmpty(itemProperties.article.currency)) {
        return itemProperties.article.currency;
    }

    // Workaround #1 - get currency of first child
    if (item.children.length !== 0) {
        const childItemProperties: eaiws.basket.ItemProperties | undefined = item.children[0].getCachedItemProperties();
        if (
            childItemProperties != null &&
            childItemProperties.article != null
        ) {
            return childItemProperties.article.currency;
        }
        return;
    }

    // Workaround #2 - get currency of first subArticle
    if (
        itemProperties.article != null &&
        itemProperties.article.subArticles != null &&
        itemProperties.article.subArticles.length > 0
    ) {
        return itemProperties.article.subArticles[0].currency;
    }
    return;
}

export function basketItemHasNoImage(value: bsk.ViewItem): boolean {
    return (
        value.type === 'Folder' || value.type === 'Text' || value.type === 'Undefined' || value.type === 'SetArticle'
    );
}

export function getBaseFilename(properties: eaiws.basket.ItemProperties | undefined, extension: string): string {
    let filename: string | undefined;
    if (properties != null && properties.article != null) {
        const tmpFilename: string = [
            getValidString(properties.article.manufacturerName, properties.article.manufacturerId),
            getValidString(properties.article.seriesName, properties.article.seriesId),
            properties.article.baseArticleNumber
        ].filter(utils.string.isNotNullOrEmpty).join('-');
        if (utils.string.isNotNullOrEmpty(tmpFilename)) {
            filename = tmpFilename;
        }
    }

    if (utils.string.isNullOrEmpty(filename)) {
        filename = t('Unknown');
    }

    return `${filename!.replace(/[^a-z0-9-]/gi, '_')}.${extension}`;
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/egr-gui-utils/CoreHelper.ts